'use client';
import { useQueryClient } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { RewardEvent } from '@flipster/utils/segment/events/reward';
import { lt } from '@flipster/utils/version';
import webviewHandler from '@flipster/utils/webview-handler';

import { LoadingPage } from '../../loading-page';
import ReferrerPage from '../../referrer-page';
import { useAuth } from '../../shared/auth/state';
import { useWebviewInfo } from '../../shared/webview';
import { PromotionList } from '../_shared/promotion-list';
import { promotionSubscriptionBunja } from '../_shared/promotion-subscription.state';
import { rewardHubV2Bunja } from './state';
import { vouchersBunja } from './vouchers/state';
import { VipTrialBanner } from './vouchers/vip-trial-banner';
import WelcomePackage from './welcome-package';

interface ChipItem {
  label: ReactNode;
  value: ChipType;
}
type ChipType = 'welcome_package' | 'referral' | 'promotion';
const Choice = createSingleChoice<ChipType>();

const RewardHubV2Page = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isCookieLoggedIn } = useAuth();

  const {
    showPromotionSubscriptionPopupAtom,
    promotionSubscriptionPopupDismissMutationAtom,
    promotionSubscriptionMutationAtom,
  } = useBunja(promotionSubscriptionBunja);
  const shouldOpenMarketingPopup = useAtomValue(showPromotionSubscriptionPopupAtom);
  const { mutateAsync: subscribePromotions } = useAtomValue(promotionSubscriptionMutationAtom);
  const { mutateAsync: dismissPopup } = useAtomValue(promotionSubscriptionPopupDismissMutationAtom);

  const { isShowRewardSectionAtom, isLoadingAtom: isRewardHubLoadingAtom } = useBunja(rewardHubV2Bunja);
  const isShowRewardSection = useAtomValue(isShowRewardSectionAtom);
  const isRewardHubLoading = useAtomValue(isRewardHubLoadingAtom);

  const { activeVouchersCountAtom, showVoucherEntryAtom, activeVipVoucherAtom, inactiveVipVoucherAtom } =
    useBunja(vouchersBunja);
  const activeVouchersCount = useAtomValue(activeVouchersCountAtom);
  const showVoucherEntry = useAtomValue(showVoucherEntryAtom);
  const activeVipVoucher = useAtomValue(activeVipVoucherAtom);
  const inactiveVipVoucher = useAtomValue(inactiveVipVoucherAtom);

  const showVipVoucherBanner = !activeVipVoucher && inactiveVipVoucher;

  const chipItems: ChipItem[] = isShowRewardSection ? chipItemsWithWelcomePackage : baseChipItems;
  const [activeTab, setActiveTab] = useState<ChipType>();

  useEffect(() => {
    setActiveTab(chipItems[0]?.value);
  }, [chipItems]);

  useEffect(() => {
    if (shouldOpenMarketingPopup && isCookieLoggedIn) {
      segment.track(RewardEvent.RewardHubPromoUpdatesView);
      dialog.open({
        title: {
          label: <FormattedMessage id='Autogen_common.r251126.s_Receive_promo_updates' />,
        },
        content: <FormattedMessage id='Autogen_common.r251126.s_Receive_promo_updates_desc' />,
        buttons: {
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={<FormattedMessage id='Common.r250401.Ok' />}
              onClick={async () => {
                segment.track(RewardEvent.RewardHubPromoUpdatesConfirmClick);
                await subscribePromotions({
                  promotionalSubscriptions: [
                    { channel: 'PUSH', subscribe: true },
                    { channel: 'EMAIL', subscribe: true },
                  ],
                });
              }}
            />
          ),
          secondary: (
            <Dialog.Button
              hierarchy='secondary'
              color='neutral'
              label={<FormattedMessage id='Common.r250922.Dont_allow' />}
              onClick={() => {
                segment.track(RewardEvent.RewardHubPromoUpdatesCancelClick);
                dismissPopup();
              }}
            />
          ),
        },
      });
    }
  }, [shouldOpenMarketingPopup, isCookieLoggedIn, dismissPopup, subscribePromotions, queryClient]);

  if (isRewardHubLoading) return <LoadingPage />;

  return (
    <main className={css({ maxW: 'desktopMaxWidth', minH: 'minHeightWithoutNavigation', mx: 'auto' })}>
      <PageHeader />
      {showVoucherEntry && (
        <div className={css({ mb: { base: 32, smallDesktop: 64 }, px: { base: 16, smallDesktop: 0 } })}>
          <button
            type='button'
            className={css({ w: '100%', cursor: 'pointer' })}
            onClick={() => {
              segment.track(RewardEvent.RewardHubYourFeeVouchersClick, {
                num_vouchers: activeVouchersCount,
              });
              webviewHandler.postMessage(
                {
                  action: 'link',
                  params: {
                    page: 'fee-vouchers',
                  },
                },
                () => router.push('/reward-hub/v2/vouchers'),
              );
            }}
          >
            <div
              className={hstack({
                w: '100%',
                py: 16,
                justify: 'space-between',
                color: 'content.default.level-5',
                textStyle: 'Text/L/Regular',
                borderBottomWidth: showVipVoucherBanner ? '0' : '1px',
                borderBottomStyle: showVipVoucherBanner ? 'none' : 'solid',
                borderColor: 'border.default.level-1',
              })}
            >
              <FormattedMessage id='VIP.r260213.Your_vouchers' />
              <div className={hstack({ gap: 8 })}>
                <p className={css({ textStyle: 'Text/L/Monospace/Strong' })}>{activeVouchersCount}</p>
                <IconButton icon={<ChevronRightIcon />} hierarchy='quaternary' size='small' />
              </div>
            </div>
          </button>
          {showVipVoucherBanner && <VipTrialBanner voucher={inactiveVipVoucher} />}
        </div>
      )}
      <Choice
        value={activeTab}
        onChange={(value) => {
          setActiveTab(value);
          segment.track(RewardEvent.RewardHubSubTabClick, {
            num_vouchers: activeVouchersCount,
            reward_hub_sub_tab_type: value,
          });
        }}
        css={{ mb: 16, px: { base: 16, smallDesktop: 0 } }}
      >
        {chipItems.map((tab) => (
          <Choice.Chip key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Choice>
      {activeTab === 'welcome_package' && <WelcomePackage />}
      {activeTab === 'referral' && <ReferrerPage hasFooter />}
      {activeTab === 'promotion' && <PromotionList />}
    </main>
  );
};

export default RewardHubV2Page;

const PageHeader = () => {
  const { appVersion, webview } = useWebviewInfo();
  if (lt(appVersion, '2.15') && webview) return null;
  return (
    <h1
      className={css({
        pt: 32,
        px: { base: 16, smallDesktop: 0 },
        pb: 20,
        textStyle: 'Display/L',
        color: 'content.default.level-5',
      })}
    >
      <FormattedMessage id='Navigation.r250401.Rewards_hub' />
    </h1>
  );
};

const baseChipItems: ChipItem[] = [
  { label: <FormattedMessage id='Reward_hub.r260128.Tabs_referral' />, value: 'referral' },
  { label: <FormattedMessage id='Reward_hub.r250213.Promotions' />, value: 'promotion' },
];

const chipItemsWithWelcomePackage: ChipItem[] = [
  { label: <FormattedMessage id='Reward_hub.r260128.Tabs_package' />, value: 'welcome_package' },
  ...baseChipItems,
];
